import type { ChartVariant, DegradationLevel } from '../chart/types.ts';

// Logika wyboru wariantu i rozmiaru czcionki, wg planu
// ~/.claude/plans/tak-a-r-b-plan-humble-crane.md, sekcja "Stopniowana
// degradacja". Warianty mierzymy LINIOWO od poziomu 0 do 7, nigdy bisekcją:
// poziom 7 nie tylko usuwa treść (scalenie sekcji), ale i DODAJE (etykiety
// scalone, licznik "×n"), więc wysokość nie jest monotoniczna względem
// poziomu. Bisekcja po niemonotonicznej funkcji wybrałaby zły wariant po
// cichu. Osiem pomiarów to koszt akceptowalny.
//
// Dopiero na WYBRANYM wariancie bisekcja rozmiaru czcionki jest uprawniona,
// bo tam monotoniczność (mniejsza czcionka = mniejsza wysokość) jest
// własnością layoutu, nie naszej konstrukcji.
//
// Ten moduł nie dotyka DOM: funkcja pomiarowa wchodzi jako parametr, żeby
// dało się ją testować na atrapie (patrz `fitLoop.test.ts`).

/**
 * Zwraca `true`, gdy `variant` wyrenderowany czcionką `fontSizePx` mieści się
 * w dostępnym polu ekranu. Implementacja produkcyjna mierzy realny DOM,
 * implementacja testowa jest atrapą.
 */
export type MeasureFn = (variant: ChartVariant, fontSizePx: number) => boolean;

export interface FitConfig {
  /** Próg, przy którym `fitLoop` szuka najbogatszego pasującego wariantu. Jedyna wartość w projekcie dobrana arbitralnie, nie zmierzona. */
  thresholdPx: number;
  /** Podłoga degradacji: poniżej tej wielkości aplikacja przechodzi w tryb awaryjny z przewijaniem, zamiast cicho zmniejszać dalej. */
  floorPx: number;
  /** Sufit: największa czcionka w ogóle rozważana. */
  ceilingPx: number;
}

// Wartości przeliczone po zmianie kroju i skali typograficznej renderera
// (kierunek "ŻAR", patrz `chart.ts`). Zmieniło się ZNACZENIE tej liczby, nie
// tylko jej wielkość: wcześniej wszystko renderowało się mniej więcej jednym
// stopniem pisma, dziś `fontSizePx` to rozmiar AKORDU, a etykiety i kotwice
// są jego ułamkiem (0,4 i 0,5 em). Ta sama liczba znaczy więc inny ekran.
//
// Sufit 24 px pochodził z sondy `tools/probe.html` i po przebudowie renderera
// stał się wiążący dla siedmiu z dziewięciu utworów korpusu, czyli obcinał
// pismo tam, gdzie miejsce jeszcze było. Zmierzone sufity poziomu 0 sięgają
// teraz 39 px (`nie-placz-ewka`), a przy zdegradowanych wariantach idą wyżej,
// więc trzymanie 24 px zostawiałoby pusty ekran przy małych akordach, czyli
// dokładnie to, na co MH słusznie narzekał po pierwszym wdrożeniu.
// Próg i podłoga zostają nietknięte i to jest wynik pomiaru, nie ostrożność.
// Podniesienie progu do 20 px zabrało kotwice „Shape of My Heart" (poziom 2
// przestawał się mieścić, wybór spadał na 7), a plan rozstrzyga ten spór
// wprost: przy graniu kotwica jest warta więcej niż większe pismo, bo po
// pierwszych słowach poznaje się, którą zwrotkę ma się przed sobą.
export const DEFAULT_FIT_CONFIG: FitConfig = {
  thresholdPx: 18,
  floorPx: 16,
  ceilingPx: 64,
};

/**
 * `ok` - jakiś wariant zmieścił się przy `thresholdPx`, renderujemy go
 * czcionką z przedziału `[thresholdPx, ceilingPx]`.
 * `zdegradowany` - żaden wariant nie mieścił się przy `thresholdPx`, ale
 * ostatni (najbardziej okrojony) wariant mieści się w paśmie
 * `[floorPx, thresholdPx)`. Interfejs pokazuje dyskretny znacznik "skrót
 * skrócony" (patrz `RenderOptions.showTruncatedBadge` w `chart.ts`).
 * `awaryjny` - nawet ostatni wariant przy `floorPx` się nie mieści. Pasmo
 * poniżej `floorPx` jest martwe (patrz plan), więc NIE schodzimy niżej po
 * cichu: aplikacja ma to nazwać wprost i przejść w tryb z przewijaniem.
 */
export type FitStatus = 'ok' | 'zdegradowany' | 'awaryjny';

export interface FitResult {
  level: DegradationLevel;
  fontSizePx: number;
  measurements: number;
  status: FitStatus;
}

/**
 * Bisekcja w górę: `low` jest ZAŁOŻONE jako już pasujące (nie jest ponownie
 * mierzone), `high` jest sufitem do sprawdzenia. Zwraca największą wartość
 * z `[low, high]`, dla której `measure(variant, x)` jest prawdziwe, przy
 * założeniu monotoniczności: im większy `x`, tym trudniej się zmieścić.
 * Ten sam algorytm służy zarówno do bisekcji w górę (od progu do sufitu),
 * jak i w dół (od podłogi do progu) - kierunek zależy tylko od tego, co
 * wywołujący poda jako `low`/`high`.
 */
function bisectSize(
  measure: (variant: ChartVariant, fontSizePx: number) => boolean,
  variant: ChartVariant,
  low: number,
  high: number,
): number {
  if (high <= low) return low;
  if (measure(variant, high)) return high;

  let lo = low;
  let hi = high;
  while (hi - lo > 1) {
    const mid = lo + Math.floor((hi - lo) / 2);
    if (measure(variant, mid)) lo = mid;
    else hi = mid;
  }
  return lo;
}

/**
 * Wybiera wariant i rozmiar czcionki dla śpiewnika jednoekranowego.
 *
 * Krok 1: mierzy WSZYSTKIE warianty liniowo, od poziomu 0, przy `thresholdPx`,
 * i zatrzymuje się na pierwszym (czyli najbogatszym), który się mieści.
 *
 * Krok 2: gdy coś się znalazło, bisekcja rozmiaru czcionki w górę do
 * `ceilingPx` NA TYM WARIANCIE.
 *
 * Krok 3: gdy nic się nie zmieściło przy `thresholdPx`, bierzemy ostatni
 * (najbardziej okrojony) wariant z listy i sprawdzamy, czy mieści się choć
 * przy `floorPx`. Jeśli tak, bisekcja w dół od `thresholdPx` do `floorPx`
 * daje status `zdegradowany`. Jeśli nawet `floorPx` nie wystarcza, status
 * `awaryjny`: nie schodzimy ciszej niżej, bo pasmo poniżej podłogi jest
 * martwe i cichy fallback do przewijania łamałby jedyną obietnicę aplikacji.
 */
export function fitLoop(
  variants: readonly ChartVariant[],
  measure: MeasureFn,
  config: FitConfig = DEFAULT_FIT_CONFIG,
): FitResult {
  let measurements = 0;
  const countedMeasure: MeasureFn = (variant, fontSizePx) => {
    measurements += 1;
    return measure(variant, fontSizePx);
  };

  let chosen: ChartVariant | null = null;
  for (const variant of variants) {
    if (countedMeasure(variant, config.thresholdPx)) {
      chosen = variant;
      break;
    }
  }

  if (chosen !== null) {
    const fontSizePx = bisectSize(countedMeasure, chosen, config.thresholdPx, config.ceilingPx);
    return { level: chosen.level, fontSizePx, measurements, status: 'ok' };
  }

  const last = variants[variants.length - 1];
  if (last === undefined) {
    // Lista wariantów jest pusta: nie ma czego renderować. Nie powinno się
    // zdarzyć na realnym `compileVariants()` (zawsze zwraca osiem), ale
    // atrapa w teście mogłaby to sprawdzić, więc zachowanie musi być jawne.
    return { level: 0, fontSizePx: config.floorPx, measurements, status: 'awaryjny' };
  }

  if (!countedMeasure(last, config.floorPx)) {
    return { level: last.level, fontSizePx: config.floorPx, measurements, status: 'awaryjny' };
  }

  const fontSizePx = bisectSize(countedMeasure, last, config.floorPx, config.thresholdPx);
  return { level: last.level, fontSizePx, measurements, status: 'zdegradowany' };
}
